import { useEffect, useState } from "react";
import Button from "@shared/v2/Button";
import { XmarkSolidV6 } from "@shared/v2/Icomoon";
import IconButton from "@shared/v2/IconButton";
import Modal from "@shared/v2/Modal";
import TextButton from "@shared/v2/TextButton";
import Typography from "@shared/v2/Typography";

interface OpportunitesTutorialModalProps {
  show: boolean;
  onClose: () => void;
}

const Video = ({ src }: { src: string }) => (
  <video
    className="tw-block tw-w-full tw-rounded-t-[6px] tw-h-[380px] tw-object-cover"
    disableRemotePlayback
    autoPlay
    muted
    loop
    src={src}
  />
);

const InfoBox = ({ children, title }: { children: React.ReactNode; title: string }) => (
  <div className="tw-text-center tw-pt-[24px] tw-px-[32px]">
    {/* @ts-expect-error props */}
    <Typography variant="headline3" className="tw-mb-[8px]">
      {title}
    </Typography>
    {/* @ts-expect-error props */}
    <Typography>{children}</Typography>
  </div>
);

const CircleButton = ({ isActive = false, onClick }: { isActive?: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    type="button"
    className={`tw-border-none tw-p-0 tw-rounded-full ${isActive ? "tw-bg-gray-base" : "tw-bg-gray-15"} tw-h-[10px] tw-w-[10px]`}
  />
);

const PAGES = [
  {
    title: "Welcome to Opportunities!",
    videoSrc: "/images/opportunities/opportunities-tutorial-1.mp4",
    description: (
      <>
        Welcome to Opportunities: your one stop shop for prospecting. On this page, you can search an area for
        Expireds, FSBO’s, and Pre-Foreclosures, each with their own list of <b>potential owners</b> to call
        and convert into a transaction.
      </>
    ),
  },
  {
    title: "Search and filter data, right in your CRM",
    videoSrc: "/images/opportunities/opportunities-tutorial-2.mp4",
    description: (
      <>
        Use the search field or draw a shape to begin searching. You can use our filters to further narrow
        down your search to find the perfect prospects.{" "}
        <i>By default, Opportunities filters out Active properties and flags DNC numbers.</i>
      </>
    ),
  },
  {
    title: "Access potential owners for each property",
    videoSrc: "/images/opportunities/opportunities-tutorial-3.mp4",
    description: (
      <>
        Each property has <b>potential owners</b> - names and phone numbers associated with that property.
        These aren’t leads <i>yet</i>, but you can easily add them right from Opportunities when you’ve
        contacted them and want to nurture the relationship.
      </>
    ),
  },
  {
    title: "Keep track of your Opportunities leads",
    imgSrc: "/images/opportunities/opportunities-tutorial-4.png",
    description: (
      <>
        Click the name of any potential owner that has been added as a lead to go to their contact detail
        page, <i>as long as you have access to the lead</i>. If another agent on your team has added them as a
        lead, you won’t be able to click or see their details.
      </>
    ),
  },
  {
    title: "Collaborate with your team",
    videoSrc: "/images/opportunities/opportunities-tutorial-5.mp4",
    description: (
      <>
        This data is shared among your team. You’ll be able to see when others on your team have already
        called an individual in the call logs.{" "}
        <i>
          We highly recommend not calling someone who’s already been called by another person on your team.
        </i>
      </>
    ),
  },
  {
    title: "Learn More",
    videoSrc: "/images/opportunities/opportunities-tutorial-6.mp4",
    description: (
      <>
        Visit the{" "}
        <a
          href="https://www.brivityknowledge.com/docs/opportunities"
          className="tw-text-theme-primary tw-font-bold"
          target="_blank"
        >
          Brivity Knowledge Center
        </a>{" "}
        to learn how to utilize Brivity Opportunities to its fullest potential with FAQ’s and best practices.
        Feel free to reach out to our Success team for any additional questions on how to make the most from
        your prospecting.
      </>
    ),
  },
];

const OpportunitiesTutorialModal: React.FC<OpportunitesTutorialModalProps> = ({ show, onClose }) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (show) setPage(0);
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-relative tw-max-w-[640px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px] tw-overflow-hidden"
    >
      {/* @ts-expect-error props */}
      <IconButton
        size="small"
        schema="transparent"
        onClick={onClose}
        className="tw-z-2 tw-absolute tw-top-[16px] tw-right-[16px] tw-bg-opacity-5 enabled:hover:!tw-bg-opacity-10"
      >
        <XmarkSolidV6 />
      </IconButton>
      <Modal.Body className="tw-flex tw-flex-col tw-gap-[8px] tw-overflow-auto tw-mt-[-32px] tw-mx-[-32px]">
        {PAGES[page]?.videoSrc && <Video src={PAGES[page]?.videoSrc} />}
        {PAGES[page]?.imgSrc && (
          <img
            className="tw-block tw-w-full tw-rounded-t-[6px] tw-h-[380px]"
            src={PAGES[page]?.imgSrc}
            alt={PAGES[page]?.title || "Tutorial Image"}
          />
        )}
        <div className="tw-flex tw-justify-center tw-gap-[8px] tw-mt-[32px]">
          {PAGES.map((_, index) => (
            <CircleButton key={index} isActive={index === page} onClick={() => setPage(index)} />
          ))}
        </div>
        <InfoBox title={PAGES[page]?.title || ""}>{PAGES[page]?.description || ""}</InfoBox>
      </Modal.Body>
      <Modal.Footer className="tw-relative tw-flex tw-justify-between tw-items-center">
        <div className="tw-absolute tw-inset-0 tw-flex tw-justify-center tw-items-center">
          {/* @ts-expect-error props */}
          <TextButton
            className={`tw-underline !tw-text-gray-50 hover:!tw-text-gray-75 ${page === PAGES.length - 1 ? "tw-invisible" : ""}`}
            size="medium"
            schema="sentence"
            onClick={onClose}
          >
            Skip Tutorial
          </TextButton>
        </div>
        {/* @ts-expect-error props */}
        <Button
          className={page < 1 ? "tw-invisible" : ""}
          size="medium"
          schema="tertiary"
          onClick={() => setPage((p) => p - 1)}
        >
          Previous
        </Button>
        {/* @ts-expect-error props */}
        <Button
          size="medium"
          onClick={() => {
            if (page === PAGES.length - 1) onClose();
            else setPage((p) => p + 1);
          }}
        >
          {page === PAGES.length - 1 ? "Get Started" : "Next"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OpportunitiesTutorialModal;
